import { Button, Input } from '@cloudflare/kumo';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createRoute } from '@tanstack/react-router';
import { useState } from 'react';
import type { CustomDomainSummary } from '../../shared/domains.ts';
import { api, queryClient, unwrap } from '../api.ts';
import { ErrorNotice, Loading } from '../common.tsx';
import { DnsRecords } from '../components/dns-records.tsx';
import { Route as parent } from './site.tsx';

export const Route = createRoute({ getParentRoute: () => parent, path: 'domain', component: Domain });

const statuses: Record<string, string> = {
  Pending: 'Preparing setup',
  DnsPending: 'Waiting for DNS',
  Provisioning: 'Setting up HTTPS and routing',
  Active: 'Connected',
  Error: 'Needs attention',
};
function Domain() {
  const { siteId } = Route.useParams();
  const [domainName, setDomainName] = useState('');
  const domains = useQuery({
    queryKey: ['domains', siteId],
    queryFn: async () => {
      const site = unwrap(
        await api.GET('/api/sites/{siteId}', {
          params: { path: { siteId } },
        }),
      );
      return { current: site.domain, pending: site.pendingDomain };
    },
    refetchInterval: 3000,
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['domains', siteId] });
  const connect = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.PUT('/api/sites/{siteId}/pending-domain', {
          params: { path: { siteId } },
          body: { pendingDomain: domainName },
        }),
      ),
    onSuccess: async (data) => {
      queryClient.setQueryData(['domains', siteId], data);
      setDomainName('');
      await refresh();
    },
  });
  return (
    <section className="stack mx-auto my-0 px-0 grid gap-5 content-start max-w-212.5 pt-7 pb-12.5 [&_.action-row]:flex-wrap [&_.content-section]:p-5 [&_.content-section]:mb-0 [&_.stack]:gap-3.5 [&_.panel-heading]:mb-0 [&_.panel-heading]:items-start [&_.panel-heading]:flex-wrap [&_h3]:m-0 [&_h3]:text-[16px] [&_h3]:font-semibold [&_h3]:wrap-anywhere [&_p]:m-0 [&_p]:leading-[1.65] [&_ol]:m-0 [&_ol]:pl-5.5 [&_ol]:list-decimal [&_li_+_li]:mt-2 min-[761px]:px-0 min-[761px]:pt-[35px] min-[761px]:pb-17.5">
      <div>
        <h2 className="text-[20px] font-semibold tracking-[-0.35px]">Domain</h2>
        <p className="muted leading-[1.65] text-kumo-subtle text-[14px]">
          Your site has one current address. A replacement takes over automatically when DNS and HTTPS are
          ready.
        </p>
      </div>
      <div className="content-section stack border border-kumo-line grid gap-5 content-start bg-kumo-base rounded-[11px] mb-5 overflow-hidden">
        <h3 className="text-[16px] font-semibold">Change site address</h3>
        <Input
          label="Custom domain"
          placeholder="www.example.com"
          value={domainName}
          onChange={(event) => setDomainName(event.target.value)}
        />
        <Button
          variant="primary"
          disabled={!domainName.trim() || connect.isPending}
          onClick={() => connect.mutate()}
        >
          {connect.isPending ? 'Preparing…' : 'Connect domain'}
        </Button>
        <p className="muted leading-[1.65] text-kumo-subtle text-[14px]">
          Activate trial or paid service in <a href="/billing">Billing</a> first. You’ll add DNS records at
          your current provider.
        </p>
        <ErrorNotice error={connect.error} />
      </div>
      <ErrorNotice error={domains.error} />
      {domains.isPending ? (
        <Loading />
      ) : (
        <>
          {domains.data && !domains.data.current.included && (
            <DomainCard
              key={domains.data.current.domain}
              siteId={siteId}
              domain={domains.data.current}
              target="current"
              refresh={refresh}
            />
          )}
          {domains.data?.pending && (
            <DomainCard
              key={domains.data.pending.domain}
              siteId={siteId}
              domain={domains.data.pending}
              target="pending"
              refresh={refresh}
            />
          )}
        </>
      )}
    </section>
  );
}
function DomainCard({
  siteId,
  domain,
  target,
  refresh,
}: {
  siteId: string;
  domain: CustomDomainSummary;
  target: 'current' | 'pending';
  refresh: () => Promise<void>;
}) {
  const check = useMutation({
    mutationFn: () =>
      api
        .POST(
          target === 'current'
            ? '/api/sites/{siteId}/current-domain/checks'
            : '/api/sites/{siteId}/pending-domain/checks',
          {
            params: { path: { siteId } },
            body: {},
          },
        )
        .then(unwrap),
    onSuccess: async (data) => {
      queryClient.setQueryData(['domains', siteId], data);
      await refresh();
    },
  });
  const disconnect = useMutation({
    mutationFn: () =>
      api
        .DELETE('/api/sites/{siteId}/domains/{domain}', {
          params: { path: { siteId, domain: domain.domain } },
          body: {},
        })
        .then(unwrap),
    onSuccess: refresh,
  });
  const { address, ownership } = domain.details.records;
  const requiredRecordsVerified =
    address &&
    [address, ...(target === 'current' ? [] : [ownership])].every(
      (record) =>
        record &&
        record.check?.status === 'Matched' &&
        (record.type !== 'A' || record.check.conflictingAAAA.length === 0),
    );
  return (
    <article
      className="content-section stack border border-kumo-line grid gap-5 content-start bg-kumo-base rounded-[11px] mb-5 overflow-hidden"
      aria-label={domain.domain}
    >
      <div className="panel-heading flex items-start justify-between gap-5 mb-[25px] [&_.muted]:mt-2 min-[761px]:items-center">
        <div>
          <h3 className="text-[16px] font-semibold">{domain.domain}</h3>
          <p className="leading-[1.65]">
            Custom domain
            {target === 'current' ? ' · Current address' : ' · Pending replacement'} ·{' '}
            {statuses[domain.status]}
          </p>
        </div>
        <div className="action-row flex gap-2 items-center">
          <Button onClick={() => check.mutate()} disabled={check.isPending}>
            Check again
          </Button>
          <Button variant="ghost" onClick={() => disconnect.mutate()} disabled={disconnect.isPending}>
            {target === 'current' ? 'Switch to a Wirechunk address' : 'Cancel replacement'}
          </Button>
        </div>
      </div>
      <ErrorNotice error={check.error ?? disconnect.error} />
      {domain.details.error && (
        <p className="leading-[1.65]" role="alert">
          {domain.details.error}
        </p>
      )}
      <ol>
        <li>Open this domain’s DNS settings.</li>
        <li>
          Add the required records. Replace A records that point elsewhere, remove AAAA records, and use
          DNS-only if your provider offers a proxy.
        </li>
        <li>Wirechunk checks DNS and enables HTTPS.</li>
      </ol>
      <DnsRecords domain={domain} />
      {requiredRecordsVerified && (
        <p className="leading-[1.65]">
          HTTPS:{' '}
          {domain.details.certificateState === 'local'
            ? 'Local development'
            : domain.details.certificateState === 'active'
              ? 'Ready'
              : domain.details.certificateState === 'failed'
                ? 'Needs attention'
                : 'Provisioning'}
        </p>
      )}
      {domain.checkedAt && (
        <p className="muted leading-[1.65] text-kumo-subtle text-[14px]">
          Last checked {new Date(domain.checkedAt).toLocaleString()}.
        </p>
      )}
    </article>
  );
}
